import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Truck, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { businessHours, socialLinks, MAPS_EMBED_URL, DIRECTIONS_URL } from '../data/business';
import { DOORDASH_URL, UBEREATS_URL, PHONE_NUMBER, EMAIL } from '../data/menu';

export function LocationContact() {
  const { t, language, isRTL } = useLanguage();

  return (
    <section 
      id="location" 
      className="py-24 sm:py-32 bg-white"
      data-testid="location-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="location-title"
          >
            {t('location.title')}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[400px] lg:h-full min-h-[400px] rounded-sm overflow-hidden shadow-lg"
          >
            <iframe
              src={MAPS_EMBED_URL}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Salam Grill Location"
              className="absolute inset-0"
              data-testid="location-map"
            />
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Address */}
            <div className="flex gap-4">
              <div className="p-3 bg-[#6B1C23]/10 h-fit rounded-full">
                <MapPin className="w-6 h-6 text-[#6B1C23]" />
              </div>
              <div>
                <p className={`font-manrope text-[#333] font-medium ${isRTL ? 'font-cairo' : ''}`}>
                  {t('location.address')}
                </p>
                <Button
                  asChild
                  variant="link"
                  className="text-[#6B1C23] hover:text-[#8B2430] p-0 h-auto font-manrope"
                  data-testid="get-directions-btn"
                >
                  <a 
                    href={DIRECTIONS_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {t('location.getDirections')} →
                  </a>
                </Button>
              </div>
            </div>

            {/* Phone */}
            <div className="flex gap-4">
              <div className="p-3 bg-[#6B1C23]/10 h-fit rounded-full">
                <Phone className="w-6 h-6 text-[#6B1C23]" />
              </div>
              <div>
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  className="font-manrope text-[#333] hover:text-[#6B1C23] transition-colors font-medium"
                  data-testid="phone-number"
                >
                  {t('location.phone')}
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex gap-4">
              <div className="p-3 bg-[#6B1C23]/10 h-fit rounded-full">
                <Mail className="w-6 h-6 text-[#6B1C23]" />
              </div>
              <div>
                <a 
                  href={`mailto:${EMAIL}`}
                  className="font-manrope text-[#333] hover:text-[#6B1C23] transition-colors font-medium"
                  data-testid="email-address"
                >
                  {EMAIL}
                </a>
              </div>
            </div>

            {/* Hours */}
            <div className="flex gap-4">
              <div className="p-3 bg-[#6B1C23]/10 h-fit rounded-full">
                <Clock className="w-6 h-6 text-[#6B1C23]" />
              </div>
              <div className="flex-1">
                <h3 className={`font-cormorant text-lg text-[#6B1C23] mb-3 font-semibold ${isRTL ? 'font-noto-serif-arabic' : ''}`}>
                  {t('location.hours')}
                </h3>
                <div className="space-y-2">
                  {businessHours.map((item, index) => (
                    <div 
                      key={item.id} 
                      className="flex justify-between font-manrope text-sm"
                      data-testid={`hours-${index}`}
                    >
                      <span className={`text-[#6B6B6B] ${isRTL ? 'font-cairo' : ''}`}>
                        {language === 'ar' ? item.dayAr : item.day}
                      </span>
                      <span className="text-[#333] font-medium">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="flex gap-4">
              <div className="p-3 bg-[#6B1C23]/10 h-fit rounded-full">
                <Truck className="w-6 h-6 text-[#6B1C23]" />
              </div>
              <div>
                <p className={`font-manrope text-[#333] mb-2 font-medium ${isRTL ? 'font-cairo' : ''}`}>
                  {t('location.delivery')}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a 
                    href={DOORDASH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    data-testid="doordash-link"
                  >
                    <span className="inline-flex items-center gap-2 bg-[#FF3008] text-white px-4 py-2 rounded-full font-manrope font-semibold text-sm hover:bg-[#E62B07] transition-colors">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.071 8.409a6.09 6.09 0 00-5.396-3.228H.584A.589.589 0 00.17 6.184L3.894 9.93a1.752 1.752 0 001.242.516h12.049a1.554 1.554 0 011.557 1.546 1.554 1.554 0 01-1.557 1.546H8.143a.59.59 0 00-.414 1.003l3.725 3.747a1.752 1.752 0 001.242.516h4.418a6.1 6.1 0 005.957-7.395z"/>
                      </svg>
                      DoorDash
                    </span>
                  </a>
                  <a 
                    href={UBEREATS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                    data-testid="ubereats-link"
                  >
                    <span className="inline-flex items-center gap-2 bg-[#142328] text-white px-4 py-2 rounded-full font-manrope font-semibold text-sm hover:bg-[#1f3640] transition-colors">
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.4c5.302 0 9.6 4.298 9.6 9.6s-4.298 9.6-9.6 9.6S2.4 17.302 2.4 12 6.698 2.4 12 2.4zm-3.6 4.8v4.8c0 1.988 1.612 3.6 3.6 3.6s3.6-1.612 3.6-3.6V7.2h-2.4v4.8c0 .662-.538 1.2-1.2 1.2s-1.2-.538-1.2-1.2V7.2H8.4z"/>
                      </svg>
                      Uber Eats
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
